import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AlertTriangle, Loader, Lock, LayoutDashboard, Users, MessageSquare, Table, BarChart3 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
// Import the actual components used in App.js
import UnifiedDashboard from './HtableDashboard';
import CDPDashboard from './CDPDashboard';
import CDPSessionView from './CDPSessionView';
import ChatbotDesigner from './ChatbotDesigner';
import HierarchicalTable from './HierarchicalTable';
import OLAPCubeBuilder from './OLAPCubeBuilder';
import { apiCall, getApiUrl } from './Api';
/**
 * Supported embed views — maps view key to component + metadata.
 * Keys match the module keys used in App.js navigation.
 */
const EMBED_VIEWS = {
  dashboard:   { component: UnifiedDashboard,  label: 'Dashboard',              icon: LayoutDashboard, path: '/dashboard' },
  cdp:         { component: CDPDashboard,       label: 'Customer Data Platform', icon: Users,           path: '/cdp' },
  chatbot:     { component: ChatbotDesigner,    label: 'Chatbot Designer',       icon: MessageSquare,   path: '/chatbot' },
  hierarchy:   { component: HierarchicalTable,  label: 'Hierarchical Analysis',  icon: Table,           path: '/hierarchy' },
  olap:        { component: OLAPCubeBuilder,    label: 'OLAP Builder',           icon: BarChart3,       path: '/olap' },
};

/** Wrapper to extract sessionId from MemoryRouter location */
const EmbedCDPSessionWrapper = ({ user, embedMode, embedConfig, onNavigate }) => {
  const location = useLocation();
  const match = location.pathname.match(/\/cdp\/session\/(?<sessionId>[^/]+)/);
  const sessionId = match?.groups?.sessionId;
  return <CDPSessionView user={user} sessionId={sessionId} onNavigate={onNavigate} embedMode={embedMode} />;
};

/**
 * EmbedView — Renders the application inside an iframe using an embed token.
 *
 * URL: /embed?token=JWT_TOKEN&view=dashboard&hideNav=false
 *
 * Supported views: dashboard, cdp, chatbot, hierarchy, olap
 *
 * The embed token is generated by the external app's backend via:
 *   POST /api/auth/embed-token { app_id, app_secret, host_tenant_id, view, ... }
 */
const EmbedView = ({ onAuthReady }) => {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [embedConfig, setEmbedConfig] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  // Sub-navigation state — handles drill-down views like cdp-session
  const [subView, setSubView] = useState(null);

  // Parse URL params once
  const urlParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      token: params.get('token'),
      view: params.get('view') || 'dashboard',
      hideNav: params.get('hideNav') === 'true',
    };
  }, []);

  // Determine which views are allowed based on module_permissions from DB
  const allowedViews = useMemo(() => {
    if (!user) return Object.keys(EMBED_VIEWS);

    const modPerms = user.module_permissions;
    if (modPerms && typeof modPerms === 'object' && Object.keys(modPerms).length > 0) {
      return Object.keys(EMBED_VIEWS).filter(key => modPerms[key]?.can_view);
    }

    // Fallback: check flat permissions list
    const perms = user.permissions;
    if (Array.isArray(perms) && perms.length > 0) {
      const moduleKeys = perms.map(p => p.split(':')[0]);
      return Object.keys(EMBED_VIEWS).filter(key => moduleKeys.includes(key));
    }

    return Object.keys(EMBED_VIEWS);
  }, [user]);

  const validateAndBootstrap = useCallback(async (token) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/auth/validate-token-app`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        setStatus('expired');
        setError('Embed token has expired. Please refresh the page in the host application.');
        return;
      }

      if (!response.ok) {
        throw new Error(`Token validation failed: ${response.status}`);
      }

      const data = await response.json();

      // Store token for subsequent API calls
      sessionStorage.setItem('embed_token', token);
      sessionStorage.setItem('embed_mode', 'true');

      // Build user-like object compatible with all child components.
      // Handles both auth modes:
      //   - "application" (app-only embed): role=host_app, user identity from app
      //   - "user" (user-scoped embed): role/username/user_id from actual user
      const isUserScoped = data.auth_mode === 'user';

      const embedUser = {
        auth_mode: data.auth_mode || 'application',
        app_id: data.app_id,
        host_tenant_id: data.host_tenant_id,
        // User-scoped: use actual user identity; App-only: use host_app defaults
        role: data.role || (isUserScoped ? 'host_user' : 'host_app'),
        username: data.username || data.user_context?.username || `app:${data.app_id}`,
        user_id: isUserScoped
          ? (data.user_id || data.user_context?.user_id || data.username)
          : `app:${data.app_id}`,
        session_id: data.session_id || null,
        permissions: data.permissions || [],
        module_permissions: data.module_permissions || {},
        embed_view: data.user_context?.embed_view || urlParams.view,
        embed_filters: data.user_context?.embed_filters || {},
        allowed_dimensions: data.user_context?.allowed_dimensions || [],
        allowed_measures: data.user_context?.allowed_measures || [],
        accessible_tenants: data.accessible_tenants || [data.host_tenant_id],
        user_context: data.user_context || {},
      };

      setUser(embedUser);
      setCurrentView(urlParams.view);
      setEmbedConfig({
        view: urlParams.view,
        filters: embedUser.embed_filters,
        allowedDimensions: embedUser.allowed_dimensions,
        allowedMeasures: embedUser.allowed_measures,
      });
      setStatus('ready');

      if (onAuthReady) onAuthReady(embedUser);

      // Notify parent window
      if (window.parent !== window) {
        window.parent.postMessage({
          type: 'EMBED_READY',
          view: urlParams.view,
          app_id: data.app_id,
          auth_mode: embedUser.auth_mode,
          username: embedUser.username,
          allowedViews: Object.keys(EMBED_VIEWS)
        }, '*');
      }
    } catch (err) {
      console.error('Embed bootstrap failed:', err);
      setStatus('error');
      setError(err.message || 'Failed to initialize embedded view');
    }
  }, [urlParams, onAuthReady]);

  useEffect(() => {
    if (!urlParams.token) {
      setStatus('error');
      setError('No embed token provided. This page must be loaded with a valid token parameter.');
      return;
    }
    validateAndBootstrap(urlParams.token);
  }, [urlParams.token, validateAndBootstrap]);

  // Listen for postMessage from parent window
  useEffect(() => {
    const handler = (event) => {
      if (!event.data || !event.data.type) return;

      switch (event.data.type) {
        case 'EMBED_UPDATE_FILTERS':
          setEmbedConfig(prev => ({
            ...prev,
            filters: { ...(prev?.filters || {}), ...event.data.filters }
          }));
          break;

        case 'EMBED_REFRESH_TOKEN':
          if (event.data.token) {
            sessionStorage.setItem('embed_token', event.data.token);
            validateAndBootstrap(event.data.token);
          }
          break;

        case 'EMBED_CHANGE_VIEW':
          if (event.data.view && EMBED_VIEWS[event.data.view]) {
            setCurrentView(event.data.view);
            setSubView(null);  // Clear sub-navigation (e.g. cdp-session detail)
            setEmbedConfig(prev => ({ ...prev, view: event.data.view }));
            if (window.parent !== window) {
              window.parent.postMessage({ type: 'EMBED_VIEW_CHANGED', view: event.data.view }, '*');
            }
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [validateAndBootstrap]);

  // Handle internal view switching
  const switchView = useCallback((viewKey) => {
    console.log('[EmbedView] switchView:', viewKey, 'clearing subView');
    if (!EMBED_VIEWS[viewKey]) return;
    setCurrentView(viewKey);
    setSubView(null);  // Clear any sub-navigation
    setEmbedConfig(prev => ({ ...prev, view: viewKey }));

    if (window.parent !== window) {
      window.parent.postMessage({ type: 'EMBED_VIEW_CHANGED', view: viewKey }, '*');
    }
  }, []);

  // Handle navigation from child components — same signature as AppLayout's handleNavigation
  const handleNavigate = useCallback((viewId, params = {}) => {
    console.log('[EmbedView] handleNavigate:', viewId, params);
    if (viewId === 'cdp-session' && params.sessionId) {
      setSubView({ type: 'cdp-session', params });
    } else if (viewId === 'cdp') {
      // Back to CDP list from session view
      switchView('cdp');
    } else if (EMBED_VIEWS[viewId]) {
      switchView(viewId);
    }
  }, [switchView]);

  // Detect modal open/close and notify parent to scroll iframe into view
  useEffect(() => {
    if (status !== 'ready' || window.parent === window) return;

    const observer = new MutationObserver(() => {
      // Detect fixed/absolute overlays (modals) — common patterns
      const modals = document.querySelectorAll('[class*="fixed"], [class*="modal"], [role="dialog"]');
      const hasModal = Array.from(modals).some(el => {
        const style = window.getComputedStyle(el);
        return style.position === 'fixed' && style.display !== 'none' && el.offsetHeight > 100;
      });

      window.parent.postMessage({
        type: 'EMBED_MODAL',
        open: hasModal
      }, '*');
    });

    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });

    return () => observer.disconnect();
  }, [status]);

  // Ref for the embed container to observe height changes
  const containerRef = useRef(null);

  // Send height to parent window so iframe resizes to fit content
  useEffect(() => {
    if (status !== 'ready' || window.parent === window) return;

    let lastHeight = 0;
    let rafId = null;

    const sendHeight = () => {
      const el = containerRef.current;
      if (!el) return;
      const height = el.scrollHeight;
      // Only send if height actually changed (avoids render loops)
      if (height !== lastHeight && height > 0) {
        lastHeight = height;
        window.parent.postMessage({ type: 'EMBED_RESIZE', height }, '*');
      }
    };

    const debouncedSend = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sendHeight);
    };

    // Observe DOM changes that affect height
    const observer = new ResizeObserver(() => debouncedSend());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Initial send + periodic check for async content
    sendHeight();
    const timer = setInterval(sendHeight, 2000);

    return () => {
      observer.disconnect();
      clearInterval(timer);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [status, currentView]);

  // ===== Render states =====

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
          <p className="mt-3 text-sm text-gray-600">Initializing embedded view...</p>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm">
          <Lock className="w-10 h-10 text-amber-500 mx-auto" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900">Session Expired</h3>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900">Embed Error</h3>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // ===== Render active view =====

  const viewDef = EMBED_VIEWS[currentView];
  const ViewComponent = viewDef?.component;

  return (
    <div ref={containerRef} className="embed-container bg-white flex flex-col min-h-screen relative">
      {/* Tab navigation — hidden if hideNav=true in URL or single view */}
      {!urlParams.hideNav && allowedViews.length > 1 && (
        <div className="bg-white border-b px-4 py-0 flex items-center justify-between shrink-0">
          <div className="flex items-center overflow-x-auto">
            {allowedViews.map(key => {
              const v = EMBED_VIEWS[key];
              const Icon = v.icon;
              const isActive = key === currentView;
              return (
                <button
                  key={key}
                  onClick={() => switchView(key)}
                  className={`flex items-center space-x-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{v.label}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center space-x-3 text-xs text-gray-400 shrink-0 pl-4">
            {user?.user_context?.company_name && (
              <span>{user.user_context.company_name}</span>
            )}
            {user?.username && !user.username.startsWith('app:') && (
              <span>• {user.username}</span>
            )}
            {user?.auth_mode === 'user' && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                {user.role}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main content — state-based rendering, no router dependency */}
      <div className="flex-1">
        {subView?.type === 'cdp-session' ? (
          <CDPSessionView
            user={user}
            sessionId={subView.params.sessionId}
            onNavigate={handleNavigate}
            embedMode={true}
          />
        ) : ViewComponent ? (
          <ViewComponent
            user={user}
            embedMode={true}
            embedConfig={embedConfig}
            onNavigate={handleNavigate}
          />
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto" />
              <p className="mt-2 text-sm text-gray-600">
                View "{currentView}" is not available
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Helper: Get the embed token for API calls.
 * Use in Api.js: const token = getEmbedToken() || getCurrentToken();
 */
export const getEmbedToken = () => {
  if (sessionStorage.getItem('embed_mode') === 'true') {
    return sessionStorage.getItem('embed_token');
  }
  return null;
};

/**
 * Helper: Check if running in embed mode
 */
export const isEmbedMode = () => {
  return sessionStorage.getItem('embed_mode') === 'true';
};

export default EmbedView;